import React, { useState } from 'react';
import { X, Calendar, Users, MapPin, CreditCard, CheckCircle, Clock, Download, Eye, ExternalLink, Mail, AlertTriangle, Globe, RefreshCw } from 'lucide-react';
import Swal from 'sweetalert2';

const API_BASE = 'https://djebooking.com';

export default function BookingsModal({ bookings, onClose, isLoading, isAdmin }) {
  const [filterHotel, setFilterHotel] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPartner, setFilterPartner] = useState('');
  const [processingId, setProcessingId] = useState(null);

  // ── Status helpers ──────────────────────────────────────────────────
  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':    return 'bg-green-100 text-green-800';
      case 'sur_demande':  return 'bg-orange-100 text-orange-800';
      case 'mygo_pending': return 'bg-purple-100 text-purple-800';
      case 'pending':      return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':    return 'bg-red-100 text-red-800';
      case 'error':        return 'bg-red-200 text-red-900';
      default:             return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'confirmed':    return <CheckCircle size={16} className="text-green-600" />;
      case 'sur_demande':  return <Clock size={16} className="text-orange-600" />;
      case 'mygo_pending': return <RefreshCw size={16} className="text-purple-600 animate-spin" />;
      case 'cancelled':    return <X size={16} className="text-red-600" />;
      case 'error':        return <AlertTriangle size={16} className="text-red-700" />;
      default:             return <Clock size={16} />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'confirmed':    return 'Confirmée';
      case 'sur_demande':  return '⏳ Sur Demande';
      case 'mygo_pending': return '🔄 En attente MyGO';
      case 'pending':      return 'En attente';
      case 'cancelled':    return 'Annulée';
      case 'error':        return '❌ Erreur MyGO';
      default:             return status;
    }
  };

  const getAuthHeader = () => ({
    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
    'Content-Type': 'application/json'
  });

  // ── resolve effective voucher URL ──────────────────────────────────
  const getEffectiveVoucherUrl = (booking) => {
    if (booking.mygoVoucherModified) {
      return `${API_BASE}/backend${booking.mygoVoucherModified}`;
    }
    return booking.mygoVoucherUrl || null;
  };

  // ── Admin: Confirm sur demande ──────────────────────────────────────
  const handleConfirm = async (bookingId, e) => {
    e.stopPropagation();

    // Prevent double-click while processing
    if (processingId === `confirm-${bookingId}`) return;

    const result = await Swal.fire({
      icon: 'question',
      title: 'Soumettre à MyGO?',
      text: 'La réservation sera transmise à MyGO pour confirmation. Le solde ne sera débité qu\'une fois MyGO confirme.',
      showCancelButton: true,
      confirmButtonText: '✅ Soumettre',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#10b981',
    });
    if (!result.isConfirmed) return;

    try {
      setProcessingId(`confirm-${bookingId}`);
      const response = await fetch(`${API_BASE}/api/reservations/${bookingId}/confirm`, {
        method: 'PATCH',
        headers: getAuthHeader()
      });
      const data = await response.json();

      // 202 means MyGO returned OnRequest — submitted but not yet confirmed
      // This covers both first click (success:true) and second click guard (success:true after fix)
      if (response.status === 202) {
        await Swal.fire({
          icon: 'info',
          title: '⏳ En attente MyGO',
          text: data.message || 'La réservation a été soumise à MyGO et attend leur confirmation. Elle sera mise à jour automatiquement toutes les 5 minutes.',
          confirmButtonColor: '#7c3aed',
          confirmButtonText: 'Compris'
        });
        window.location.reload();
        return;
      }

      if (!response.ok) throw new Error(data.error || 'Erreur lors de la confirmation');

      await Swal.fire({ icon: 'success', title: 'Réservation confirmée!', timer: 2000, showConfirmButton: false });
      window.location.reload();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: error.message, confirmButtonColor: '#FF7B47' });
    } finally {
      setProcessingId(null);
    }
  };

  // ── Admin: Sync MyGO status for a pending booking ──────────────────
  const handleSyncMyGO = async (booking, e) => {
    e.stopPropagation();

    if (processingId === `sync-${booking.id}`) return;

    try {
      setProcessingId(`sync-${booking.id}`);
      const response = await fetch(`${API_BASE}/api/reservations/${booking.id}/sync-mygo`, {
        method: 'PATCH',
        headers: getAuthHeader()
      });
      const data = await response.json();

      if (response.status === 202) {
        await Swal.fire({
          icon: 'info',
          title: '⏳ Toujours en attente',
          text: data.message || 'MyGO n\'a pas encore confirmé cette réservation.',
          confirmButtonColor: '#7c3aed'
        });
        return;
      }

      if (!response.ok) throw new Error(data.error || 'Erreur de synchronisation');

      if (data.mygoState === 'Validated') {
        await Swal.fire({
          icon: 'success',
          title: '✅ Confirmée par MyGO!',
          text: data.message,
          timer: 3000,
          showConfirmButton: false
        });
        window.location.reload();
      } else {
        await Swal.fire({
          icon: 'warning',
          title: 'Annulée par MyGO',
          text: data.message,
          confirmButtonColor: '#FF7B47'
        });
        window.location.reload();
      }
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: error.message, confirmButtonColor: '#FF7B47' });
    } finally {
      setProcessingId(null);
    }
  };

  // ── Admin: Reject sur demande ───────────────────────────────────────
  const handleReject = async (bookingId, e) => {
    e.stopPropagation();

    if (processingId === `reject-${bookingId}`) return;

    const { value: notes, isConfirmed } = await Swal.fire({
      icon: 'warning',
      title: 'Rejeter cette réservation?',
      html: `
        <p style="margin-bottom:10px;color:#666;">Le partenaire sera notifié. Aucun solde ne sera débité.</p>
        <textarea id="reject-notes" class="swal2-textarea" placeholder="Motif du refus (optionnel)..." style="height:100px;"></textarea>
      `,
      showCancelButton: true,
      confirmButtonText: '❌ Rejeter',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#dc3545',
      preConfirm: () => document.getElementById('reject-notes').value
    });
    if (!isConfirmed) return;

    try {
      setProcessingId(`reject-${bookingId}`);
      const response = await fetch(`${API_BASE}/api/reservations/${bookingId}/reject`, {
        method: 'PATCH',
        headers: getAuthHeader(),
        body: JSON.stringify({ adminNotes: notes })
      });
      if (!response.ok) throw new Error('Erreur lors du rejet');

      await Swal.fire({ icon: 'success', title: 'Réservation rejetée', timer: 2000, showConfirmButton: false });
      window.location.reload();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: error.message, confirmButtonColor: '#FF7B47' });
    } finally {
      setProcessingId(null);
    }
  };

  // ── Partner/Admin: Cancel confirmed booking ────────────────────────
  const handleCancel = async (booking, e) => {
    e.stopPropagation();

    if (processingId === `cancel-${booking.id}`) return;

    let feeData;
    try {
      setProcessingId(`cancel-${booking.id}`);
      const feeRes = await fetch(`${API_BASE}/api/reservations/${booking.id}/cancellation-fees`, {
        headers: getAuthHeader()
      });
      feeData = await feeRes.json();
      if (!feeRes.ok) throw new Error(feeData.error || 'Impossible de vérifier les frais');
    } catch (err) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: err.message, confirmButtonColor: '#FF7B47' });
      setProcessingId(null);
      return;
    } finally {
      setProcessingId(null);
    }

    const feeHtml = feeData.mygoFee > 0
      ? `<p style="color:#dc3545;font-weight:bold;">⚠️ Frais d'annulation: <strong>${feeData.mygoFee.toFixed(3)} TND</strong></p>
         <p>Remboursement: <strong>${feeData.refundAmount.toFixed(3)} TND</strong></p>`
      : `<p style="color:#10b981;font-weight:bold;">✅ Annulation sans frais — remboursement complet</p>
         <p>Remboursement: <strong>${feeData.montantAchat.toFixed(3)} TND</strong></p>`;

    const confirm = await Swal.fire({
      icon: feeData.mygoFee > 0 ? 'warning' : 'question',
      title: 'Confirmer l\'annulation?',
      html: `
        <div style="text-align:left;">
          <p><strong>Hôtel:</strong> ${booking.hotelName}</p>
          <p><strong>Réf:</strong> ${booking.refCommande}</p>
          <p><strong>Séjour:</strong> ${booking.checkIn} → ${booking.checkOut}</p>
          <hr style="margin:12px 0;"/>
          ${feeHtml}
          <p style="color:#666;font-size:13px;margin-top:12px;">Cette action est irréversible.</p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: '❌ Annuler la réservation',
      cancelButtonText: 'Retour',
      confirmButtonColor: '#dc3545',
      cancelButtonColor: '#6b7280',
    });

    if (!confirm.isConfirmed) return;

    try {
      setProcessingId(`cancel-${booking.id}`);
      const response = await fetch(`${API_BASE}/api/reservations/${booking.id}/cancel`, {
        method: 'PATCH',
        headers: getAuthHeader()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erreur lors de l\'annulation');

      const successMsg = data.refundAmount > 0
        ? `Remboursement de <strong>${data.refundAmount.toFixed(3)} TND</strong> crédité sur votre solde.`
        : 'Aucun remboursement applicable.';

      await Swal.fire({ icon: 'success', title: 'Réservation annulée', html: successMsg, timer: 3000, showConfirmButton: false });
      window.location.reload();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: error.message, confirmButtonColor: '#FF7B47' });
    } finally {
      setProcessingId(null);
    }
  };

  const handleRecoverMyGO = async (bookingId, e) => {
  e.stopPropagation();
  if (processingId === `recover-${bookingId}`) return;

  const { value: mygoId, isConfirmed } = await Swal.fire({
    icon: 'question',
    title: 'Récupérer depuis MyGO',
    html: `<p style="margin-bottom:10px;color:#666;">Entrez l'ID de booking MyGO à lier à cette réservation.</p>
           <input id="mygo-id-input" class="swal2-input" placeholder="Ex: 394721" type="number">`,
    showCancelButton: true,
    confirmButtonText: '🔗 Récupérer',
    cancelButtonText: 'Annuler',
    confirmButtonColor: '#7c3aed',
    preConfirm: () => {
      const val = document.getElementById('mygo-id-input').value;
      if (!val) { Swal.showValidationMessage('ID MyGO requis'); return false; }
      return val;
    }
  });

  if (!isConfirmed || !mygoId) return;

  try {
    setProcessingId(`recover-${bookingId}`);
    const response = await fetch(`${API_BASE}/api/reservations/${bookingId}/recover-mygo`, {
      method: 'PATCH',
      headers: getAuthHeader(),
      body: JSON.stringify({ mygoBookingId: mygoId })
    });
    const data = await response.json();
    if (!response.ok) throw new Error(data.error || 'Erreur lors de la récupération');
    await Swal.fire({ 
      icon: 'success', 
      title: '✅ Réservation récupérée!', 
      text: data.message, 
      timer: 3000, 
      showConfirmButton: false 
    });
    window.location.reload();
  } catch (error) {
    Swal.fire({ icon: 'error', title: 'Erreur', text: error.message, confirmButtonColor: '#FF7B47' });
  } finally {
    setProcessingId(null);
  }
};

  // ── View / Download / Send voucher ────────────────────────────────
  const handleViewVoucher = (booking, e) => {
    e.stopPropagation();
    const url = getEffectiveVoucherUrl(booking);
    if (!url) {
      Swal.fire({ icon: 'info', title: 'Voucher indisponible', text: 'Le voucher n\'est pas encore disponible.', confirmButtonColor: '#FF7B47' });
      return;
    }
    window.open(url, '_blank');
  };

  const handleDownloadVoucher = (booking, e) => {
    e.stopPropagation();
    const url = getEffectiveVoucherUrl(booking);
    if (!url) {
      Swal.fire({ icon: 'info', title: 'Voucher indisponible', text: 'Le voucher n\'est pas encore disponible.', confirmButtonColor: '#FF7B47' });
      return;
    }
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.download = `voucher-${booking.refCommande}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSendVoucher = async (booking, e) => {
    e.stopPropagation();
    const url = getEffectiveVoucherUrl(booking);
    if (!url) {
      Swal.fire({ icon: 'info', title: 'Voucher indisponible', text: 'Le voucher n\'est pas encore disponible.', confirmButtonColor: '#FF7B47' });
      return;
    }

    const result = await Swal.fire({
      icon: 'question',
      title: 'Envoyer le voucher par email?',
      html: `Le voucher sera envoyé à <strong>${booking.clientEmail || 'l\'email du client'}</strong>`,
      showCancelButton: true,
      confirmButtonText: '📧 Envoyer',
      cancelButtonText: 'Annuler',
      confirmButtonColor: '#2B4979',
    });
    if (!result.isConfirmed) return;

    try {
      setProcessingId(`voucher-${booking.id}`);
      const response = await fetch(`${API_BASE}/api/reservations/${booking.id}/send-voucher`, {
        method: 'POST',
        headers: getAuthHeader()
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Erreur lors de l\'envoi');

      Swal.fire({ icon: 'success', title: 'Email envoyé!', text: data.message, timer: 2500, showConfirmButton: false });
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Erreur', text: error.message, confirmButtonColor: '#FF7B47' });
    } finally {
      setProcessingId(null);
    }
  };

  // ── Filter ──────────────────────────────────────────────────────────
  const filteredBookings = bookings.filter(booking => {
    const matchesHotel   = !filterHotel   || booking.hotelName.toLowerCase().includes(filterHotel.toLowerCase());
    const matchesStatus  = filterStatus === 'all' || booking.status === filterStatus;
    const matchesPartner = !filterPartner || (booking.partnerName || '').toLowerCase().includes(filterPartner.toLowerCase());
    return matchesHotel && matchesStatus && matchesPartner;
  });

  // ── Render ──────────────────────────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">

        {/* Header */}
        <div className="bg-gradient-to-r from-[#2B4979] to-[#3d5c8f] text-white p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold">Mes Réservations</h2>
            <button onClick={onClose} className="hover:bg-white/20 p-2 rounded-full transition">
              <X size={24} />
            </button>
          </div>

          <div className={`grid grid-cols-1 gap-4 ${isAdmin ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
            <div>
              <label className="block text-sm mb-2 text-blue-100">Rechercher par hôtel</label>
              <input
                type="text"
                placeholder="Nom de l'hôtel..."
                value={filterHotel}
                onChange={(e) => setFilterHotel(e.target.value)}
                className="w-full px-4 py-2 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF7B47]"
              />
            </div>

            {isAdmin && (
              <div>
                <label className="block text-sm mb-2 text-blue-100">Rechercher par partenaire</label>
                <input
                  type="text"
                  placeholder="Nom du partenaire..."
                  value={filterPartner}
                  onChange={(e) => setFilterPartner(e.target.value)}
                  className="w-full px-4 py-2 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF7B47]"
                />
              </div>
            )}

            <div>
              <label className="block text-sm mb-2 text-blue-100">Statut</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="w-full px-4 py-2 rounded-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#FF7B47]"
              >
                <option value="all">Tous les statuts</option>
                <option value="confirmed">Confirmées</option>
                <option value="sur_demande">⏳ Sur Demande</option>
                <option value="mygo_pending">🔄 En attente MyGO</option>
                <option value="cancelled">Annulées</option>
                <option value="error">❌ Erreur MyGO</option>
              </select>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#FF7B47]"></div>
            </div>
          ) : filteredBookings.length === 0 ? (
            <div className="text-center py-12">
              <Calendar size={64} className="mx-auto text-gray-400 mb-4" />
              <p className="text-xl text-gray-600">
                {filterHotel || filterStatus !== 'all'
                  ? 'Aucune réservation ne correspond aux filtres'
                  : 'Aucune réservation trouvée'}
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {filteredBookings.map((booking) => (
                <div
                  key={booking.id}
                  className="border-2 border-gray-200 rounded-xl p-6 bg-white shadow-sm hover:shadow-md transition"
                >
                  {/* Booking Header */}
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-xl font-bold text-[#2B4979]">{booking.hotelName}</h3>
                        <span className={`px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${getStatusColor(booking.status)}`}>
                          {getStatusIcon(booking.status)}
                          {getStatusText(booking.status)}
                        </span>
                        {booking.mygoVoucherModified && (
                          <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs font-bold rounded-full flex items-center gap-1">
                            <Globe size={11} />
                            Voucher B2B prêt
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-3 flex-wrap">
                        <p className="text-gray-600 flex items-center gap-2">
                          <MapPin size={16} />
                          {booking.location}
                        </p>
                        {isAdmin && booking.partnerName && (
                          <span className="bg-[#2B4979]/10 text-[#2B4979] text-xs font-semibold px-3 py-1 rounded-full border border-[#2B4979]/20 flex items-center gap-1">
                            <Users size={12} />
                            {booking.partnerName}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Réf: {booking.refCommande}</p>
                      {/* <p className="text-sm text-gray-500">ID: {booking.bookingId}</p> */}
                      <p className="text-sm text-gray-500">Réservé le: {booking.bookingDate}</p>
                    </div>
                  </div>

                  {/* Booking Details Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                    <div className="flex items-center gap-2 text-gray-700">
                      <Calendar size={18} className="text-[#FF7B47]" />
                      <div>
                        <p className="text-xs text-gray-500">Dates</p>
                        <p className="font-semibold">{booking.checkIn} → {booking.checkOut}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <Users size={18} className="text-[#FF7B47]" />
                      <div>
                        <p className="text-xs text-gray-500">Voyageurs</p>
                        <p className="font-semibold">{booking.guests}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-gray-700">
                      <CreditCard size={18} className="text-[#FF7B47]" />
                      <div>
                        <p className="text-xs text-gray-500">Paiement</p>
                        <p className="font-semibold">{booking.paymentStatus}</p>
                      </div>
                    </div>
                  </div>

                  {/* Client Info */}
                  <div className="bg-gray-50 rounded-lg p-4 mb-4">
                    <h4 className="font-semibold text-[#2B4979] mb-2">Informations client</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                      <p><span className="font-semibold">Nom:</span> {booking.clientName}</p>
                      <p><span className="font-semibold">Email:</span> {booking.clientEmail || '—'}</p>
                      <p><span className="font-semibold">Tél:</span> {booking.clientPhone || '—'}</p>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="border-t-2 border-gray-200 pt-4 mb-4">
                    <div className="bg-gradient-to-br from-[#2B4979] to-[#3d5c8f] text-white rounded-lg p-4">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-xs opacity-80">Prix total</p>
                          <p className="text-2xl font-bold">{parseFloat(booking.totalPrice).toFixed(3)} TND</p>
                        </div>
                        <CreditCard size={32} className="opacity-50" />
                      </div>
                    </div>
                  </div>

                  {/* Room Details */}
                  {booking.details && booking.details.length > 0 && (
                    <div className="mb-4">
                      <h4 className="font-semibold text-[#2B4979] mb-3">Détails des chambres ({booking.rooms})</h4>
                      <div className="space-y-2">
                        {booking.details.map((detail, idx) => (
                          <div key={idx} className="bg-gray-50 rounded-lg p-3 text-sm">
                            <div className="flex justify-between items-center">
                              <div>
                                <p className="font-semibold text-[#2B4979]">{detail.room}</p>
                                <p className="text-gray-600">{detail.pension}</p>
                                <p className="text-xs text-gray-500">
                                  {detail.nb_adl} adulte(s){detail.nb_enf > 0 && `, ${detail.nb_enf} enfant(s)`}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-xs text-gray-500">Prix chambre</p>
                                <p className="font-bold text-[#FF7B47]">{parseFloat(detail.montant_vente).toFixed(3)} TND</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* ── Action Buttons ── */}
                  <div className="pt-4 border-t space-y-3">

                    {/* Sur Demande: Admin sees Confirm / Reject */}
                    {isAdmin && booking.status === 'sur_demande' && (
                      <div className="flex gap-3">
                        <button
                          onClick={(e) => handleConfirm(booking.id, e)}
                          disabled={processingId === `confirm-${booking.id}`}
                          className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-50 text-white py-2 px-4 rounded-lg transition font-semibold flex items-center justify-center gap-2"
                        >
                          {processingId === `confirm-${booking.id}`
                            ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                            : <CheckCircle size={18} />}
                          Soumettre à MyGO
                        </button>
                        <button
                          onClick={(e) => handleReject(booking.id, e)}
                          disabled={processingId === `reject-${booking.id}`}
                          className="flex-1 bg-red-500 hover:bg-red-600 disabled:opacity-50 text-white py-2 px-4 rounded-lg transition font-semibold flex items-center justify-center gap-2"
                        >
                          {processingId === `reject-${booking.id}`
                            ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                            : <X size={18} />}
                          Rejeter
                        </button>
                      </div>
                    )}

                    {/* MyGO_En_Attente — admin only sees sync button */}
                    {isAdmin && booking.status === 'mygo_pending' && (
                      <div className="space-y-2">
                        <div className="flex items-center gap-2 text-xs text-purple-700 bg-purple-50 border border-purple-200 rounded-lg px-3 py-2">
                          <RefreshCw size={13} className="animate-spin" />
                          Soumis à MyGO — en attente de leur décision (vérification auto toutes les 5 min)
                        </div>
                        <button
                          onClick={(e) => handleSyncMyGO(booking, e)}
                          disabled={processingId === `sync-${booking.id}`}
                          className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg transition font-semibold flex items-center justify-center gap-2"
                        >
                          {processingId === `sync-${booking.id}`
                            ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                            : <RefreshCw size={18} />}
                          Vérifier statut MyGO maintenant
                        </button>
                      </div>
                    )}

                    {/* Confirmed: Voucher buttons + Cancel */}
                    {booking.status === 'confirmed' && (
                      <>
                        <div className="flex gap-3 flex-wrap">
                          <button
                            onClick={(e) => handleViewVoucher(booking, e)}
                            className={`flex-1 text-white py-2 px-4 rounded-lg transition font-semibold flex items-center justify-center gap-2 ${
                              booking.mygoVoucherModified
                                ? 'bg-blue-600 hover:bg-blue-700'
                                : 'bg-[#2B4979] hover:bg-[#1e3454]'
                            }`}
                          >
                            <ExternalLink size={18} />
                            {booking.mygoVoucherModified ? 'Voir voucher B2B' : 'Voir voucher'}
                          </button>

                          <button
                            onClick={(e) => handleDownloadVoucher(booking, e)}
                            className="flex-1 bg-[#FF7B47] hover:bg-[#ff6a30] text-white py-2 px-4 rounded-lg transition font-semibold flex items-center justify-center gap-2"
                          >
                            <Download size={18} />
                            Télécharger
                          </button>

                          <button
                            onClick={(e) => handleSendVoucher(booking, e)}
                            disabled={processingId === `voucher-${booking.id}`}
                            className="flex-1 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg transition font-semibold flex items-center justify-center gap-2"
                          >
                            {processingId === `voucher-${booking.id}`
                              ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                              : <Mail size={18} />}
                            Envoyer au client
                          </button>
                        </div>

                        {!booking.mygoVoucherModified && booking.isB2B && (
                          <div className="flex items-center gap-2 text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-2">
                            <Clock size={13} />
                            Voucher B2B en cours de génération…
                          </div>
                        )}

                        <button
                          onClick={(e) => handleCancel(booking, e)}
                          disabled={processingId === `cancel-${booking.id}`}
                          className="w-full bg-red-50 hover:bg-red-100 disabled:opacity-50 text-red-600 border border-red-200 py-2 px-4 rounded-lg transition font-semibold flex items-center justify-center gap-2"
                        >
                          {processingId === `cancel-${booking.id}`
                            ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600" />
                            : <AlertTriangle size={18} />}
                          Demander l'annulation
                        </button>
                      </>
                    )}

                    {/* Cancelled */}
                    {booking.status === 'cancelled' && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-center gap-2">
                        <X size={16} />
                        Cette réservation a été annulée.
                      </div>
                    )}

                    {/* Error — MyGO failed during creation */}
{booking.status === 'error' && (
  <div className="space-y-2">
    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700 flex items-start gap-2">
      <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
      <div>
        <p className="font-semibold">Erreur MyGO lors de la réservation</p>
        <p className="text-xs mt-1 text-red-600">
          MyGO a rejeté ou n'a pas pu traiter cette demande. Le solde n'a pas été débité.
          Référence: {booking.refCommande}
        </p>
      </div>
    </div>
    {isAdmin && (
      <>
        <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
          Si MyGO a en réalité validé cette réservation, entrez l'ID MyGO pour récupérer.
        </div>
        <button
          onClick={(e) => handleRecoverMyGO(booking.id, e)}
          disabled={processingId === `recover-${booking.id}`}
          className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50 text-white py-2 px-4 rounded-lg transition font-semibold flex items-center justify-center gap-2"
        >
          {processingId === `recover-${booking.id}`
            ? <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
            : <RefreshCw size={18} />}
          Récupérer depuis MyGO
        </button>
      </>
    )}
  </div>
)}

                  </div>

                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t bg-gray-50 p-4 flex justify-between items-center">
          <p className="text-sm text-gray-600">
            Total: <span className="font-bold text-[#2B4979]">{filteredBookings.length}</span> réservation(s)
            {(filterHotel || filterStatus !== 'all') && (
              <span className="ml-2 text-[#FF7B47]">(filtrées sur {bookings.length})</span>
            )}
          </p>
          <button
            onClick={onClose}
            className="bg-[#2B4979] hover:bg-[#1e3454] text-white px-6 py-2 rounded-lg transition font-semibold"
          >
            Fermer
          </button>
        </div>
      </div>
    </div>
  );
}